import React from 'react';
import { GenerativeBlockRenderer } from 'flare-generative-ui';
import { MessageBubble } from './MessageBubble.jsx';
import { MessageArtifactCard } from './MessageArtifactCard.jsx';
import { MessagePatchPreviewCard } from './MessagePatchPreviewCard.jsx';
import { MessagePatchQuestionNodeCard } from './MessagePatchQuestionNodeCard.jsx';
import { MessagePatchApplyStatusCard } from './MessagePatchApplyStatusCard.jsx';
import { MessageArtifactPresentationPolicyCard } from './MessageArtifactPresentationPolicyCard.jsx';
import { MessageAttachmentContextRow } from './MessageAttachmentContextRow.jsx';
import { ThinkingCard } from './ThinkingCard.jsx';
import AssistantTurn from './AssistantTurn.jsx';
import { renderTimelineDraftChooserNode } from './chatTimelineDraftChooserRenderer.js';
import {
  isThinkingCompleted,
  isThinkingWaitingUser,
} from './thinkingState.js';

function resolveThinkingHistorical(item, hasStreamingContent) {
  if (hasStreamingContent) {
    return false;
  }
  if (isThinkingWaitingUser(item)) {
    return false;
  }
  return isThinkingCompleted(item);
}

function resolveMessageRegenerateHandler(onRegenerate, item) {
  if (typeof onRegenerate !== 'function') {
    return undefined;
  }
  return () => onRegenerate({ messageId: item.id });
}

function resolveThinkingRegenerateHandler(onRegenerate) {
  if (typeof onRegenerate !== 'function') {
    return undefined;
  }
  return () => onRegenerate();
}

function suppressReconciliationTrace(type) {
  switch (type) {
    case 'reconciliation_trace':
      return null;
    default:
      return undefined;
  }
}

export function renderTimelineItemNode({
  item,
  assistantName,
  hasStreamingContent,
  latestRegenerableItemId,
  onRegenerate,
  onMessageCapabilityAction,
  onUICardAction,
  onWorkflowOperationAction,
  registry,
  skillProjection,
  renderMessageAvatar,
  themeTokens,
  withinAssistantTurn = false,
}) {
  const generativeTypes = new Set([
    'ui_cards',
    'sourcing_candidates',
    'risk_summary',
    'shortlist_updated',
    'evaluation_report_ready',
    'plan_block',
    'knowledge_citation',
    'knowledge_search',
    'mode_switch_reason',
  ]);
  const renderGenerativeBlock = () => (
    <div className="flare-assistant-meta-row">
      <div style={{ maxWidth: '68%', width: '100%' }}>
        <GenerativeBlockRenderer
          item={item}
          onAction={onUICardAction}
          registry={registry}
          themeTokens={themeTokens}
        />
      </div>
    </div>
  );
  const renderers = new Map([
    ['assistant_turn', () => (
      <AssistantTurn
        item={item}
        assistantName={assistantName}
        renderMessageAvatar={renderMessageAvatar}
        themeTokens={themeTokens}
        renderItemNode={(child) => renderTimelineItemNode({
          item: child, assistantName, hasStreamingContent, latestRegenerableItemId,
          onRegenerate, onMessageCapabilityAction, onUICardAction, onWorkflowOperationAction,
          registry, skillProjection, renderMessageAvatar, themeTokens, withinAssistantTurn: true,
        })}
      />
    )],
    ['message', () => (
      <>
        <MessageAttachmentContextRow attachments={item.attachments} role={item.role} themeTokens={themeTokens} />
        <MessageBubble
          showIdentity={!withinAssistantTurn}
          assistantName={assistantName}
          capabilityActions={item.messageCapabilityActions}
          content={item.content}
          citationPayload={item.citationPayload}
          onUICardAction={onUICardAction}
          createdAt={item.createdAt}
          onRegenerate={resolveMessageRegenerateHandler(onRegenerate, item)}
          onCapabilityAction={onMessageCapabilityAction}
          regenerateEnabled={item.id === latestRegenerableItemId && !hasStreamingContent}
          messageIdentityKey={item.messageIdentityKey}
          renderMessageAvatar={renderMessageAvatar}
          role={item.role}
          themeTokens={themeTokens}
        />
      </>
    )],
    ['streaming', () => (
      <MessageBubble
        showIdentity={!withinAssistantTurn}
        assistantName={assistantName}
        content={item.content}
        citationPayload={item.citationPayload}
        onUICardAction={onUICardAction}
        messageIdentityKey={item.messageIdentityKey}
        onRegenerate={onRegenerate}
        renderMessageAvatar={renderMessageAvatar}
        role="assistant"
        streaming={item.generating === true}
        themeTokens={themeTokens}
      />
    )],
    ['thinking', () => (
      <ThinkingCard
        showIdentity={!withinAssistantTurn}
        assistantName={assistantName}
        renderMessageAvatar={renderMessageAvatar}
        hasStreamingContent={hasStreamingContent}
        historical={resolveThinkingHistorical(item, hasStreamingContent)}
        item={item}
        onRegenerate={resolveThinkingRegenerateHandler(onRegenerate)}
        onUICardAction={onUICardAction}
        onWorkflowOperationAction={onWorkflowOperationAction}
        skillProjection={skillProjection}
        regenerateEnabled={item.id === latestRegenerableItemId && !hasStreamingContent}
        themeTokens={themeTokens}
      />
    )],
    ['execution', () => null],
    ['message_artifact', () => (
      <div className="flare-assistant-meta-row">
        <div style={{ maxWidth: new Map([['committed_artifact', '100%']]).get(item.presentation_kind) ?? '54.4%', width: '100%' }}>
          <MessageArtifactCard item={item} onAction={onUICardAction} skillProjection={skillProjection} themeTokens={themeTokens} />
        </div>
      </div>
    )],
    ['patch_preview', () => (
      <div className="flare-assistant-meta-row">
        <div style={{ maxWidth: '68%', width: '100%' }}>
          <MessagePatchPreviewCard item={item} onAction={onUICardAction} themeTokens={themeTokens} />
        </div>
      </div>
    )],
    ['patch_question_node', () => (
      <div className="flare-assistant-meta-row">
        <div style={{ maxWidth: '68%', width: '100%' }}>
          <MessagePatchQuestionNodeCard item={item} onAction={onUICardAction} themeTokens={themeTokens} />
        </div>
      </div>
    )],
    ['patch_apply_status', () => (
      <div className="flare-assistant-meta-row">
        <div style={{ maxWidth: '68%', width: '100%' }}>
          <MessagePatchApplyStatusCard item={item} themeTokens={themeTokens} />
        </div>
      </div>
    )],
    ['reconciliation_trace', () => suppressReconciliationTrace(item.type)],
    ['artifact_presentation_policy', () => (
      <div className="flare-assistant-meta-row">
        <div style={{ maxWidth: '68%', width: '100%' }}>
          <MessageArtifactPresentationPolicyCard item={item} themeTokens={themeTokens} />
        </div>
      </div>
    )],
    ['orchestration_status', () => renderTimelineDraftChooserNode({
      item,
      themeTokens,
      defaultExpanded: hasStreamingContent,
    })],
  ]);
  const renderer = renderers.get(item.type) ?? (generativeTypes.has(item.type) ? renderGenerativeBlock : null);
  return typeof renderer === 'function' ? renderer() : null;
}
